import React from "react"

import {
	Box,
	Paper,
	Popover,
} from "@mui/material"
import { Theme } from "@mui/material/styles"
import { SxProps } from "@mui/system"


import { DateTime } from "luxon"
import {
	plural,
} from "@lingui/macro"

import {
	PaginatedResponse,
	SessionResponse,
} from "models"
import {
	DashboardCard,
} from "components/dashboard"
import DashboardActionsList from "./dashboard-actions-list"
import withUseRouter, { UseRouterParams } from "../../../services/router"
import { MatesComponent, MatesComponentState } from "../../mates-component"
import { DashboardCardInfo, DashboardCardsData } from "components/dashboard/dashboard-card/models"
import { DashboardHotSituations } from "components/dashboard/dashboard-card-with-list/dashboard-hot-situations"
import DashboardVisitsList from "components/dashboard/dashboard-card-with-list/dashboard-visits-list"

export interface DashboardCardWithListProps extends UseRouterParams {
	cardType: DashboardCardInfo
	navigateTo: string
	data: PaginatedResponse<any>
	sessionData: SessionResponse
	showListInPopup?: boolean
	refreshPage: () => void
	brand?: boolean
}
export interface DashboardCardWithListState extends MatesComponentState {
	todayActionsCount: number
	allPendingActionsCount: number

	openPopover: boolean
	openCreation: boolean
}
class DashboardCardWithList extends React.Component<DashboardCardWithListProps, DashboardCardWithListState> {
	private readonly cardRef: React.RefObject<HTMLButtonElement> = React.createRef()
	private readonly listRef: React.RefObject<any> = React.createRef()
	private readonly matesComponent: MatesComponent<DashboardCardWithListProps, DashboardCardWithListState> = new MatesComponent(this)

	static defaultProps = {
		showListInPopup: false,
		brand: false
	}

	constructor(props: DashboardCardWithListProps) {
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			todayActionsCount: 0,
			allPendingActionsCount: 0,
			openPopover: false,
			openCreation: false,
		}
	}

	public componentDidMount() {
		this.updateCounts()
	}

	private updateCounts = () => {
		let totalRecords = this.props.data.items.length
		let today = DateTime.now();
		let todayItems: any[] // Correggiamo il tipo qui

		todayItems = this.props.data.items
			.map(it => ({
				item: it,
				date: DateTime.fromISO(it.deadline || it.dataOra) // Supporta sia actions (deadline) che visits (dataOra)
			}))
			.filter(it => it.date.hasSame(today, "day") && it.date.hasSame(today, "month") && it.date.hasSame(today, "year"))
			.sort((a, b) => b.date.toMillis() - a.date.toMillis())
			.map(it => it.item);

		this.setState(() => ({
			todayActionsCount: todayItems.length,
			allPendingActionsCount: totalRecords - todayItems.length,
		}));
	}

	private calculateNumberOfActions = () => {
		if(this.props.cardType === DashboardCardsData.ACTIONS) {
			return plural(
				this.state.todayActionsCount,
				{
					one: "# PROMEMORIA",
					other: "# PROMEMORIA",
				},
			)
		}

		return this.state.todayActionsCount.toString()
	}

	private onCardClick = (): void => {
		if (this.props.showListInPopup !== true) {
			return
		}

		this.setState(() => ({ openPopover: true }))
	}

	private onPopoverClose = (): void => {
		this.setState(() => ({ openPopover: false }))
	}

	render() {
		let paperSx: SxProps<Theme> = {
			height: "330px",
			border: (DashboardCardsData.HOT_ASSETS !== this.props.cardType && DashboardCardsData.HOT_BRAND !== this.props.cardType) ? `1px solid ${this.props.cardType.avatarColour}` : "none",
			mt: -4,
		}
		const isHotCard = this.props.cardType === DashboardCardsData.HOT_ASSETS || this.props.cardType === DashboardCardsData.HOT_BRAND
		const notificationsValue = isHotCard ? undefined : this.state.allPendingActionsCount
		const cardValue = isHotCard
			? this.state.allPendingActionsCount
			: this.calculateNumberOfActions()

		return (
			<>
				<Box
					ref={this.cardRef}
					onClick={this.onCardClick}
				>
					<DashboardCard
						navigateTo={this.props.navigateTo}
						cardType={this.props.cardType}
						value={cardValue}
						notifications={notificationsValue}
						showNotificationsBadge={!isHotCard}
					/>
				</Box>

				<Popover
					open={this.state.openPopover}
					anchorEl={this.cardRef.current}
					anchorReference="anchorEl"
					onClose={this.onPopoverClose}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "center",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "center",
					}}
					slotProps={{
						paper: {
							sx: {
								width: this.cardRef.current?.offsetWidth ?? 0,
							},
						},
					}}
				>
					<Paper elevation={0} sx={paperSx} ref={this.listRef}>
						{
							DashboardCardsData.ACTIONS === this.props.cardType &&
							<DashboardActionsList
								actions={this.props.data.items}
								sessionData={this.props.sessionData}
								refresh={this.props.refreshPage}
							/>
						}
						{
							DashboardCardsData.VISITS === this.props.cardType &&
							<DashboardVisitsList
								visits={this.props.data.items}
								sessionData={this.props.sessionData}
								refresh={this.props.refreshPage}
							/>
						}
						{
							DashboardCardsData.HOT_ASSETS === this.props.cardType &&
							<DashboardHotSituations
								cardType={"ASSET"}
								data={this.props.data.items}
							/>
						}
						{
							DashboardCardsData.HOT_BRAND === this.props.cardType &&
							<DashboardHotSituations
								cardType={"BRAND"}
								data={this.props.data.items}
							/>
						}
					</Paper>
				</Popover>

				{
					!this.props.showListInPopup &&
					<>
						<Paper elevation={0} sx={paperSx} ref={this.listRef}>
							{
								DashboardCardsData.ACTIONS === this.props.cardType &&
								<DashboardActionsList
									actions={this.props.data.items}
									sessionData={this.props.sessionData}
									refresh={this.props.refreshPage}
								/>
							}
							{
								DashboardCardsData.VISITS === this.props.cardType &&
								<DashboardVisitsList
									visits={this.props.data.items}
									sessionData={this.props.sessionData}
									refresh={this.props.refreshPage}
								/>
							}
							{
								DashboardCardsData.HOT_ASSETS === this.props.cardType &&
								<DashboardHotSituations
									cardType={"ASSET"}
									data={this.props.data.items.map((match) => match.match)}
								/>
							}
							{
								DashboardCardsData.HOT_BRAND === this.props.cardType &&
								<DashboardHotSituations
									cardType={"BRAND"}
									data={this.props.data.items}
								/>
							}
						</Paper>
					</>
				}
			</>
		)
	}
}

export default withUseRouter(DashboardCardWithList)
